import { useMemo, useCallback, memo } from 'react'
import { TableCell, TableRow } from '@shared/components/ui/table'
import { Checkbox } from '@shared/components/ui/checkbox'
import { cn } from '@shared/lib/utils'
import type { Column, TableAction, TableActions, TableActionGroup } from '@shared/types/data-table'
import { Cell } from './Cell'
import { ActionDropdown } from '@shared/components/ui/action-dropdown/ActionDropdown'
import { HoverActions } from './HoverActions'
import { stickyRowCellVariants } from './sticky-column-variants'

const TRANSITION_CLASSES = "transition-colors duration-200 ease-in-out"

interface RowProps<T> {
  item: T
  index: number
  itemId: string
  columns: Column<T>[]
  selectedItems: string[]
  unavailableItems: string[]
  rowActions?: TableActions<T> | ((item: T) => TableActions<T>)
  hoverActions?: TableAction<T>[] | ((item: T) => TableAction<T>[])
  onRowClick?: (item: T) => void
  onToggleSelection: (itemId: string) => void
  showRightShadow: boolean
  getItemStatus?: (item: T) => string
  allowedStatuses?: string[]
  activeItemId?: string
  getRowClassName?: (item: T) => string | undefined
}

function RowComponent<T extends Record<string, unknown>>({
  item,
  index,
  itemId,
  columns,
  selectedItems,
  unavailableItems = [],
  rowActions,
  hoverActions,
  onRowClick,
  onToggleSelection,
  showRightShadow,
  getItemStatus,
  allowedStatuses = [],
  activeItemId,
  getRowClassName,
}: RowProps<T>) {
  const isSelected = selectedItems.includes(itemId)
  const isUnavailable = unavailableItems.includes(itemId)
  const isActive = activeItemId === itemId

  const itemStatus = getItemStatus?.(item)
  const isSelectableByStatus = !getItemStatus || !allowedStatuses.length || allowedStatuses.includes(itemStatus || '')
  const isSelectable = isSelectableByStatus && !isUnavailable

  const resolvedRowActions = useMemo(() => {
    if (!rowActions) return []
    return typeof rowActions === 'function' ? rowActions(item) : rowActions
  }, [rowActions, item])

  const isGrouped = resolvedRowActions.length > 0 && 'label' in resolvedRowActions[0] && 'actions' in resolvedRowActions[0]

  const hasVisibleRowActions = useMemo(() => {
    if (!resolvedRowActions.length) return false
    if (isGrouped) {
      return (resolvedRowActions as TableActionGroup<T>[]).some(g =>
        g.actions.some(a => !a.condition || a.condition(item))
      )
    }
    return (resolvedRowActions as TableAction<T>[]).some(a => !a.condition || a.condition(item))
  }, [resolvedRowActions, isGrouped, item])

  const visibleHoverActions = useMemo(() => {
    const actions = hoverActions ? (typeof hoverActions === 'function' ? hoverActions(item) : hoverActions) : []
    return actions.filter(action => !action.condition || action.condition(item))
  }, [hoverActions, item])

  const handleToggle = useCallback(() => {
    if (isSelectable) onToggleSelection(itemId)
  }, [isSelectable, onToggleSelection, itemId])

  const handleRowClick = useCallback(() => {
    if (onRowClick) onRowClick(item)
  }, [onRowClick, item])

  const handleSelectionClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    handleToggle()
  }, [handleToggle])

  const rowHighlightCls = !isActive && !isSelected ? getRowClassName?.(item) : undefined

  return (
    <TableRow
      selected={isSelected}
      data-row-id={itemId}
      data-active={isActive}
      className={cn(
        "group cursor-pointer", TRANSITION_CLASSES,
        isActive && "bg-primary-light dark:bg-primary-light hover:bg-primary-light/80 dark:hover:bg-primary-light/60 border-l-4 border-l-primary",
        isSelected && !isActive && "bg-gray-100 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800",
        !isActive && !isSelected && (rowHighlightCls ?? "hover:bg-gray-50 dark:hover:bg-gray-800/50")
      )}
      onClick={handleRowClick}
      tabIndex={onRowClick ? 0 : undefined}
      aria-selected={isSelected}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleRowClick()
        }
      }}
    >
      <TableCell
        className="w-9 min-w-9 max-w-9 px-1 text-center bg-inherit"
        onClick={handleSelectionClick}
        role="gridcell"
      >
        <div className="flex items-center justify-center">
          <Checkbox
            checked={isSelected}
            disabled={!isSelectable}
            onCheckedChange={handleToggle}
            className={!isSelectable ? "opacity-50 cursor-not-allowed" : ""}
            aria-label={`${isSelected ? 'Deselect' : 'Select'} row ${index + 1}`}
          />
        </div>
      </TableCell>

      {columns.map((column) => (
        <Cell
          key={column.key as string}
          column={column}
          item={item}
          index={index}
        />
      ))}

      <TableCell
        className="w-full min-w-24 relative bg-inherit p-0"
        role="gridcell"
      >
        {visibleHoverActions.length > 0 && (
          <div className="absolute inset-0 flex items-center justify-end pr-3">
            <HoverActions actions={visibleHoverActions} item={item} />
          </div>
        )}
      </TableCell>

      {hasVisibleRowActions && (
        <TableCell
          className={cn(
            stickyRowCellVariants({
              showRightShadow
            })
          )}
          data-sticky-action="true"
          onClick={(e) => e.stopPropagation()}
          role="gridcell"
        >
          <div className="flex items-center justify-center">
            <ActionDropdown
              actions={resolvedRowActions}
              item={item}
            />
          </div>
        </TableCell>
      )}

    </TableRow>
  )
}

export const Row = memo(RowComponent) as <T extends Record<string, unknown>>(props: RowProps<T>) => JSX.Element
